import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Users } from 'lucide-react'
import { billingAPI } from '../../services/api'
import { t } from '../../i18n'
import { SERVICES } from '../../config/services'
import { planPrice, planServicesText, planUsersText, featuredIndex, isHeading } from '../../config/plans'
import { PLAN_CURRENCY_STORAGE_KEY, planCurrencyForCountry } from '../../config/planCurrencies'
import PlanCurrencySelector from '../../components/PlanCurrencySelector'

const INCLUDED = [
  'Your own website and subdomain',
  'Arabic and English',
  'Orders, bookings and customers',
  'Loyalty points',
  'Cash, card and bank transfer',
  'No commission on your sales',
]

// The platform's live plans (Platform > Plans). Before any plan exists it
// shows what every account includes. `compare` adds the side-by-side table.
export default function PricingSection({ heading = true, compare = false }) {
  const [plans, setPlans] = useState(null)
  const [priceCountry, setPriceCountry] = useState(() => localStorage.getItem(PLAN_CURRENCY_STORAGE_KEY) || '')
  const displayCurrency = planCurrencyForCountry(priceCountry)?.currency
  const changePriceCountry = value => {
    setPriceCountry(value)
    if (value) localStorage.setItem(PLAN_CURRENCY_STORAGE_KEY, value)
    else localStorage.removeItem(PLAN_CURRENCY_STORAGE_KEY)
  }
  useEffect(() => { billingAPI.getPublicPlans().then(r => setPlans(r.data.plans || [])).catch(() => setPlans([])) }, [])

  return (
    <section id="pricing" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      {heading && (
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-semibold text-blue-700">{t('Pricing')}</p>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">{t('Simple monthly plans. No commission.')}</h2>
          <p className="mt-3 text-lg text-slate-600">{t('Start with a free trial, then pick the plan that fits. Change or cancel any time.')}</p>
        </div>
      )}

      <PlanCurrencySelector country={priceCountry} onChange={changePriceCountry} className="mx-auto mt-8 max-w-3xl" />

      {plans && plans.length > 0 ? (
        <>
          <div className={`mt-12 grid gap-6 ${plans.length >= 3 ? 'lg:grid-cols-3' : plans.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'max-w-md mx-auto'}`}>
            {plans.map((plan, i) => {
              const featured = i === featuredIndex(plans)
              return (
                <div key={plan.id} className={`relative flex flex-col rounded-2xl border bg-white p-7 ${featured ? 'border-blue-700 shadow-xl shadow-blue-900/10' : 'border-slate-200'}`}>
                  {featured && <span className="absolute -top-3 start-7 rounded-full bg-blue-700 px-3 py-1 text-xs font-semibold text-white">{t('Most popular')}</span>}
                  <h3 className="text-lg font-bold">{t(plan.name)}</h3>
                  <p className="mt-4 flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold tracking-tight">{planPrice(plan, displayCurrency)}</span>
                    <span className="text-slate-500">/ {t(plan.interval === 'year' ? 'year' : 'month')}</span>
                  </p>
                  <p className="mt-5 text-sm font-semibold text-blue-700">{planServicesText(plan)}</p>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-700"><Users className="h-4 w-4 text-slate-400" />{planUsersText(plan)}</p>
                  <ul className="mt-5 flex-1 space-y-3 text-sm text-slate-700">
                    {(plan.features.length ? plan.features : INCLUDED.slice(0, 4)).map(f => isHeading(f) ? <li key={f} className="pt-1 text-xs font-semibold text-slate-500">{t(f)}</li> : <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />{t(f)}</li>)}
                  </ul>
                  <Link to={`/get-started?plan=${plan.id}`} className={`mt-8 rounded-xl py-3 text-center font-semibold ${featured ? 'bg-blue-700 text-white hover:bg-blue-800' : 'border border-slate-300 text-slate-900 hover:bg-slate-50'}`}>{t('Start free trial')}</Link>
                </div>
              )
            })}
          </div>
          <p className="mt-6 text-center text-sm text-slate-500">
            {t('Services: {list}.', { list: SERVICES.map(s => t(s.title)).join(t(', ')) })}{' '}
            {t('WhatsApp marketing campaigns can be added to any plan for an extra monthly fee.')}
          </p>
          {compare && <CompareTable plans={plans} displayCurrency={displayCurrency} />}
        </>
      ) : (
        <div className="mx-auto mt-12 max-w-3xl rounded-2xl border border-slate-200 p-8 sm:p-10">
          <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <h3 className="text-xl font-bold">{t('Every account includes')}</h3>
              <ul className="mt-5 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
                {INCLUDED.map(f => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />{t(f)}</li>)}
              </ul>
            </div>
            <Link to="/get-started" className="rounded-xl bg-blue-700 px-6 py-3 text-center font-semibold text-white hover:bg-blue-800">{t('Start free trial')}</Link>
          </div>
        </div>
      )}
    </section>
  )
}

// Plans side by side, one row per thing that differs.
function CompareTable({ plans, displayCurrency }) {
  const rows = [
    ['Monthly price', p => <strong>{planPrice(p, displayCurrency)}</strong>],
    ['Services', p => planServicesText(p)],
    ['Users', p => planUsersText(p)],
    ['Loyalty points', () => <Check className="mx-auto h-5 w-5 text-blue-700" aria-label={t('Included')} />],
    ['WhatsApp marketing', () => <span className="text-slate-500">{t('Paid add-on')}</span>],
    ['Also included', p => <ul className="space-y-1">{p.features.filter(f => !isHeading(f)).map(f => <li key={f}>{t(f)}</li>)}</ul>],
  ]
  return (
    <div className="mt-16">
      <h3 className="text-center text-2xl font-bold">{t('Compare plans')}</h3>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th scope="col" className="p-4 text-start font-semibold text-slate-500">{t('Plan')}</th>
              {plans.map(p => <th key={p.id} scope="col" className="p-4 text-center font-bold text-slate-900">{t(p.name)}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(([label, cell]) => (
              <tr key={label}>
                <th scope="row" className="p-4 text-start font-semibold text-slate-700">{t(label)}</th>
                {plans.map(p => <td key={p.id} className="p-4 text-center text-slate-700 align-top">{cell(p)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
